import React from 'react';
import styled from 'styled-components';
import { Plus, Trash2, AlertCircle } from 'lucide-react';
import type { InvoiceLineItem } from '../../../../../Data/invoice.dto';
import { formatINR } from '../../../../../Utils/helper';

interface LineItemEditorProps {
  items: InvoiceLineItem[];
  onChange: (items: InvoiceLineItem[]) => void;
  withoutPrice?: boolean;
}

const emptyItem = (index: number): InvoiceLineItem => ({
  description: '',
  quantity: 1,
  unit_price: '',
  order_index: index,
});

const LineItemEditor: React.FC<LineItemEditorProps> = ({ items, onChange, withoutPrice = false }) => {
  const handleAdd = () => {
    onChange([...items, emptyItem(items.length)]);
  };

  const handleRemove = (idx: number) => {
    const updated = items.filter((_, i) => i !== idx).map((item, i) => ({ ...item, order_index: i }));
    onChange(updated);
  };

  const handleChange = (idx: number, field: keyof InvoiceLineItem, value: string | number) => {
    let sanitizedVal = value;
    if (field === 'unit_price') {
      const parsed = parseFloat(String(value));
      sanitizedVal = isNaN(parsed) ? '' : String(Math.max(0, parsed));
    }
    const updated = items.map((item, i) =>
      i === idx ? { ...item, [field]: sanitizedVal } : item
    );
    onChange(updated);
  };

  const lineTotal = (item: InvoiceLineItem): string => {
    const total = item.quantity * parseFloat(item.unit_price || '0');
    return isNaN(total) ? '0.00' : formatINR(total);
  };

  return (
    <Wrapper>
      <TableWrap>
        <Table>
          <thead>
            <tr>
              <Th style={{ width: 32 }}>#</Th>
              <Th>Description</Th>
              <Th style={{ width: 80 }}>Qty</Th>
              {!withoutPrice && <Th style={{ width: 120 }}>Unit Price (₹)</Th>}
              {!withoutPrice && <Th style={{ width: 120, textAlign: 'right' }}>Total (₹)</Th>}
              <Th style={{ width: 44 }}></Th>
            </tr>
          </thead>
          <tbody>
            {items.length === 0 ? (
              <tr>
                <td colSpan={withoutPrice ? 4 : 6} style={{ textAlign: 'center', padding: 20, color: 'rgba(23,23,23,0.42)', fontFamily: 'Montserrat,sans-serif', fontSize: 13 }}>
                  No items yet. Click "Add Item" below.
                </td>
              </tr>
            ) : (
              items.map((item, idx) => (
                <tr key={idx}>
                  <Td style={{ color: 'rgba(23,23,23,0.45)', fontWeight: 600, fontSize: 13 }}>{idx + 1}</Td>
                  <Td>
                    <Input
                      type="text"
                      value={item.description}
                      onChange={e => handleChange(idx, 'description', e.target.value)}
                      placeholder="Item description"
                    />
                  </Td>
                  <Td>
                    <Input
                      type="number"
                      min={1}
                      value={item.quantity}
                      onChange={e => handleChange(idx, 'quantity', Number(e.target.value))}
                      style={{ textAlign: 'center' }}
                    />
                  </Td>
                  {!withoutPrice && (
                    <Td>
                      <Input
                        type="number"
                        min={0}
                        step="0.01"
                        value={item.unit_price}
                        onChange={e => handleChange(idx, 'unit_price', e.target.value)}
                        placeholder="Enter Price"
                        style={{ textAlign: 'right' }}
                      />
                    </Td>
                  )}
                  {!withoutPrice && (
                    <Td style={{ textAlign: 'right', fontWeight: 700, color: '#171717' }}>
                      ₹{lineTotal(item)}
                    </Td>
                  )}
                  <Td>
                    <RemoveBtn type="button" onClick={() => handleRemove(idx)} aria-label="Remove item">
                      <Trash2 size={14} />
                    </RemoveBtn>
                  </Td>
                </tr>
              ))
            )}
          </tbody>
        </Table>
      </TableWrap>

      <AddItemRow>
        <AddBtn type="button" onClick={handleAdd}>
          <Plus size={15} />
          Add Item
        </AddBtn>
        {!withoutPrice && items.length > 0 && (
          <SubtotalText>
            Subtotal: ₹{formatINR(items.reduce((sum, item) => sum + item.quantity * parseFloat(item.unit_price || '0'), 0))}
          </SubtotalText>
        )}
      </AddItemRow>
    </Wrapper>
  );
};

export default LineItemEditor;

// ─── Styled Components ────────────────────────────────────────

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const TableWrap = styled.div`
  overflow-x: auto;
  border-radius: 10px;
  border: 1px solid rgba(23, 23, 23, 0.08);
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  font-family: Urbanist, sans-serif;
`;

const Th = styled.th`
  padding: 10px 12px;
  background: rgba(23, 23, 23, 0.04);
  font-family: Montserrat, sans-serif;
  font-size: 12px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: rgba(23, 23, 23, 0.55);
  text-align: left;
  white-space: nowrap;
`;

const Td = styled.td`
  padding: 8px 12px;
  border-top: 1px solid rgba(23, 23, 23, 0.05);
  vertical-align: middle;
`;

const Input = styled.input`
  width: 100%;
  padding: 7px 10px;
  border: 1px solid rgba(23, 23, 23, 0.12);
  border-radius: 8px;
  font-family: Urbanist, sans-serif;
  font-size: 14px;
  color: #171717;
  background: #fafafa;
  outline: none;
  box-sizing: border-box;

  &:focus {
    border-color: #c94daa;
    background: #fff;
  }
`;

const RemoveBtn = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  border: none;
  border-radius: 7px;
  background: rgba(228, 72, 98, 0.1);
  color: #e44862;
  cursor: pointer;
  transition: background 0.15s;

  &:hover {
    background: rgba(228, 72, 98, 0.2);
  }
`;

const AddItemRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const AddBtn = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 8px 14px;
  border: 1.5px dashed rgba(201, 77, 170, 0.5);
  border-radius: 8px;
  background: rgba(201, 77, 170, 0.04);
  color: #c94daa;
  font-family: Montserrat, sans-serif;
  font-size: 13px;
  font-weight: 700;
  cursor: pointer;
  transition: all 0.15s;

  &:hover {
    border-color: #c94daa;
    background: rgba(201, 77, 170, 0.1);
  }
`;

const SubtotalText = styled.span`
  font-family: Montserrat, sans-serif;
  font-size: 13px;
  font-weight: 700;
  color: rgba(23, 23, 23, 0.75);
`;
